<template>
  <div class="employee-inspection-requests">
    <AsEmployeeInspectionBanner
      v-if="showBanner"
      class="employee-inspection-requests__banner"
      :state="bannerState"
      :inspector="bannerInspector"
      :reason="bannerReason"
      :readiness-level="bannerReadinessLevel"
      :expedited="bannerExpedited"
      @onRequest="handleBannerRequest"
      @onRetry="handleBannerRetry"
      @onCancel="handleBannerCancel"
      @onChatter="handleBannerChatter"
    />

    <p v-if="bannerError" class="employee-inspection-requests__error" role="alert">
      {{ bannerError }}
    </p>

    <AsEmployeeInspectionRequestsTable
      :data="inspectionRequests"
      @onCancel="handleCancel"
      @onPrepare="handlePrepare"
      @onChatterClick="handleChatterClick"
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import AsEmployeeInspectionBanner from "@components/AsEmployeeInspectionBanner/AsEmployeeInspectionBanner.vue";
import AsEmployeeInspectionRequestsTable from "@components/AsEmployeeInspectionRequestsTable/AsEmployeeInspectionRequestsTable.vue";
import { LD_FLAG_IDEA_MDP_176 } from "@components/constants/launchDarklyFlagKeys";
import { cancelInspectionRequest, dismissInspectionRequest, retryInspectionRequest } from "@/api";

const props = defineProps({
  /**
   * Latest undismissed inspection request, returned by the ER-12319 banner
   * query. The query is not wired yet; when it ships, this prop is fed from
   * its result. `null` hides the banner.
   */
  bannerRequest: {
    type: Object,
    default: () => null,
  },
  /** Set when the employee holds an Architect badge for the Responsibility. */
  isArchitect: {
    type: Boolean,
    default: false,
  },
  /** Overrides the account id resolved from localStorage (test convenience). */
  accountId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(["onRequestInspection", "onChatterClick"]);

// ER-11504 (the as-employee list endpoint) is still Blocked — there is no feed
// to fetch yet. The table renders its own empty state until that backend
// ticket ships and this view is wired to the list.
const inspectionRequests = ref([]);

// idea-MDP-176 gates the whole list banner.
const bannerEnabled = ref(false);
const { proxy } = getCurrentInstance() || {};

onMounted(async () => {
  try {
    bannerEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_176, false)) === true;
  } catch (error) {
    console.error("Failed to resolve idea-MDP-176 flag for the banner:", error);
  }
});

const TERMINAL_OUTCOME_STATES = ["declined", "auto_declined", "failed", "failed_coach_unavailable"];

/**
 * Maps a banner-query request to an AsEmployeeInspectionBanner state.
 * `failed_coach_unavailable` is `failed` with `coach_is_current === false`;
 * `auto_declined` is `declined` with `declined_by === "system"`.
 */
function deriveBannerState(request) {
  if (!request || !request.status) return null;
  const status = String(request.status).toLowerCase();
  if (status === "pending" || status === "accepted") return status;
  if (status === "declined") {
    return String(request.declined_by || "").toLowerCase() === "system"
      ? "auto_declined"
      : "declined";
  }
  if (status === "failed") {
    return request.coach_is_current === false ? "failed_coach_unavailable" : "failed";
  }
  if (status === "none") {
    return request.has_inspectors === false ? "no_inspectors" : "make_request";
  }
  return null;
}

const bannerState = ref(deriveBannerState(props.bannerRequest));
const bannerError = ref("");

watch(
  () => props.bannerRequest,
  (request) => {
    bannerState.value = deriveBannerState(request);
    bannerError.value = "";
  }
);

const showBanner = computed(() => bannerEnabled.value && !props.isArchitect && !!bannerState.value);

const bannerInspector = computed(() => {
  const request = props.bannerRequest;
  if (!request) return null;
  return request.assigned_to || request.inspector || null;
});

const bannerReason = computed(() => {
  const reason = props.bannerRequest?.reason;
  return reason ? String(reason) : "";
});

const bannerReadinessLevel = computed(
  () =>
    props.bannerRequest?.readiness_level || props.bannerRequest?.readinessLevel || "Professional"
);

const bannerExpedited = computed(() => props.bannerRequest?.expedited === true);

function resolveAccountId() {
  if (props.accountId != null) return props.accountId;
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function resolveResponsibilityId() {
  const request = props.bannerRequest;
  if (!request) return null;
  return request.responsibility_id ?? request.responsibilityId ?? request.responsibility ?? null;
}

function resolveEvaluationId() {
  return props.bannerRequest?.id ?? props.bannerRequest?.evaluation_id ?? null;
}

function extractErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data === "object") {
    const field = data.detail || data.message || data.error || data.non_field_errors;
    if (Array.isArray(field)) return field[0];
    if (field) return field;
  }
  return "Unable to retry the inspection request.";
}

// Rule 22 — retry a failed request. On success the banner moves to Pending for
// the new request; on the "no longer a coach" 400 it moves to
// Failed - Coach Unavailable and surfaces the error message.
async function handleBannerRetry() {
  const accountId = resolveAccountId();
  const responsibilityId = resolveResponsibilityId();
  const evaluationId = resolveEvaluationId();
  if (accountId == null || responsibilityId == null || evaluationId == null) {
    return;
  }

  bannerError.value = "";
  try {
    await retryInspectionRequest(accountId, responsibilityId, evaluationId);
    bannerState.value = "pending";
  } catch (error) {
    const status = error?.response?.status;
    if (status === 400) {
      bannerState.value = "failed_coach_unavailable";
    }
    bannerError.value = extractErrorMessage(error);
  }
}

// Rule 12 — Cancel on a terminal outcome only dismisses the banner (the status
// stays declined/failed). Cancel on pending/accepted keeps cancelling the request.
async function handleBannerCancel() {
  const accountId = resolveAccountId();
  const responsibilityId = resolveResponsibilityId();
  const evaluationId = resolveEvaluationId();
  if (accountId == null || responsibilityId == null || evaluationId == null) {
    return;
  }

  bannerError.value = "";
  try {
    if (TERMINAL_OUTCOME_STATES.includes(bannerState.value)) {
      await dismissInspectionRequest(accountId, responsibilityId, evaluationId);
    } else {
      await cancelInspectionRequest(accountId, responsibilityId, evaluationId);
    }
    bannerState.value = "make_request";
  } catch (error) {
    bannerError.value = extractErrorMessage(error);
  }
}

// The Request Inspection dialog (with the coach picker) and the chatter pane are
// opened by the parent. The list view has no per-request responsibility context
// yet (ER-11504/ER-12260), so these intents are forwarded upward.
const handleBannerRequest = () => {
  emit("onRequestInspection", props.bannerRequest);
};

const handleBannerChatter = () => {
  emit("onChatterClick", props.bannerRequest);
};

// Rule 12 — employee Cancel from pending/accepted, no reason required.
// Wires to the as-employee cancel action once ER-11504 exposes it.
const handleCancel = () => {
  // TODO(ER-11504): call the as-employee cancel action.
};

// Destination not yet confirmed with design (ticket AC3) — the table only
// emits intent; wire the real navigation once it is decided.
const handlePrepare = () => {
  // TODO(ER-11510): navigate to the Prepare destination once confirmed with design.
};

const handleChatterClick = () => {
  // TODO(ER-11504): open the request chatter once the feed ships.
};
</script>

<style scoped>
.employee-inspection-requests {
  width: 100%;
}

.employee-inspection-requests__banner {
  margin-bottom: 16px;
}

.employee-inspection-requests__error {
  margin: 0 0 16px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fef3f2;
  border: 1px solid #fecdca;
  color: #b42318;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
}
</style>
